export type ENVS = 'dev' | 'stg' | 'prd';

import type {
  Account,
  AccountInvite,
  AccountUser,
  Aircraft,
  Cargo,
  Contact,
  Document,
  FlightSegment,
  FulfillmentPartnerConfiguration,
  Instruction,
  LineItem,
  Provider,
  ProviderAccount,
  ProviderDataMap,
  Quote,
  QuoteVariant,
  Rate,
  RateImport,
  Request,
  Surcharge,
  TieredCost,
  User,
} from '@prisma/client';
import {
  AccountType,
  AircraftRestriction,
  AircraftType,
  BacklogStatus,
  BatteryType,
  BusinessType,
  CargoCooling,
  CargoCoolingRange,
  CargoTypeSub,
  CostUnitType,
  DimensionUnit,
  DocumentType,
  EarliestTenderOption,
  ExpiryReason,
  FreightService,
  FreightServiceAddOn,
  FreightStatus,
  HandlingOptions,
  LineItemType,
  MarkupCategory,
  NotificationCategory,
  OnboardingStep,
  PackingGroup,
  PackingInstruction,
  ProviderDataMapType,
  QuoteStatus,
  RateCategory,
  RateImportState,
  RequestStatus,
  Section,
  ServiceType,
  SourceType,
  SurchargeCategory,
  SurchargeUnit,
  SurchargeWeightType,
  TimeOfDay,
  TrackingShipmentStatus,
  VariantType,
  WeightUnit,
} from '@prisma/client';

// Prisma doesn't export enums that are define in schema but not used in schema
enum InlandCategory {
  PICK_UP = 'PICK_UP',
  AWB_LABELING = 'AWB_LABELING',
  TRANSFER = 'TRANSFER',
}

export {
  Account,
  AccountInvite,
  AccountType,
  AccountUser,
  Aircraft,
  AircraftRestriction,
  AircraftType,
  BacklogStatus,
  BatteryType,
  BusinessType,
  Cargo,
  CargoCooling,
  CargoCoolingRange,
  CargoTypeSub,
  Contact,
  CostUnitType,
  DimensionUnit,
  Document,
  DocumentType,
  EarliestTenderOption,
  ExpiryReason,
  FlightSegment,
  FreightService,
  FreightServiceAddOn,
  FreightStatus,
  FulfillmentPartnerConfiguration,
  HandlingOptions,
  InlandCategory,
  Instruction,
  LineItem,
  LineItemType,
  MarkupCategory,
  NotificationCategory,
  OnboardingStep,
  PackingGroup,
  PackingInstruction,
  Provider,
  ProviderAccount,
  ProviderDataMap,
  ProviderDataMapType,
  Quote,
  QuoteStatus,
  QuoteVariant,
  Rate,
  RateCategory,
  RateImport,
  RateImportState,
  Request,
  RequestStatus,
  Section,
  ServiceType,
  SourceType,
  Surcharge,
  SurchargeCategory,
  SurchargeUnit,
  SurchargeWeightType,
  TieredCost,
  TimeOfDay,
  TrackingShipmentStatus,
  User,
  VariantType,
  WeightUnit,
};

export enum TypesenseCollection {
  ACCOUNTS = 'accounts',
  AIRPORTS = 'airports',
  AIRLINES = 'airlines',
  AIRCRAFTS = 'aircrafts',
  REQUESTS = 'requests',
  CONTACTS = 'contacts',
}

export enum CargoType {
  // PrismaCargoType
  GeneralCargo = 'GeneralCargo',
  Perishable = 'Perishable',
  DangerousGoods = 'DangerousGoods',
  Pharma = 'Pharma',
  Valuable = 'Valuable',

  // UI Cargo Type
  Batteries = 'Batteries',
}

export enum DataType {
  'STRING' = 'STRING',
  'NUMBER' = 'NUMBER',
  'DATE' = 'DATE',
  'BOOLEAN' = 'BOOLEAN',
  'JSON' = 'JSON',
  'IGNORE' = 'IGNORE',
}

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export type RequireOnly<T, K extends keyof T> = {
  [Key in K]: T[Key];
} & {
  [Key in Exclude<keyof T, K>]?: T[Key];
};

export type ValueOf<T> = T[keyof T];

export type KeysOfUnion<T> = T extends T ? keyof T : never;

export type KeysMatching<T extends object, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

type Impossible<K extends keyof any> = {
  [P in K]: never;
};

export type Exact<T, U extends T = T> = U & Impossible<Exclude<keyof U, keyof T>>;
export type OptionalField<T, K extends keyof T> = Exclude<T, K> & { [Key in K]?: T[Key] };

// Copied from flight segment reorder branch
type TypeWithGeneric<T> = T[];
type FuncWithGeneric<T> = (arg: T) => any;
export type ExtractGeneric<Type> = Type extends TypeWithGeneric<infer X> ? X : never;
export type ExtractGenericFromFunction<Type extends Function> = Type extends FuncWithGeneric<infer X> ? X : never;

export type ExtractTypeFromTRPCSubscription<T extends { subscribe: <T>(T) => any }> = Parameters<
  ExtractGenericFromFunction<T['subscribe']>['next']
>[0];

interface HasId {
  id: string | number;
}

export const keysOf = <Arg extends object>(arg: Arg) => Object.keys(arg) as (keyof Arg)[];

export type Shell<T extends HasId> = RequireOnly<T, 'id'>;

// Typesense
export type TSAirport = {
  id: string;
  airport_name: string;
  airport_time_zone: string;
  airport_id: string;
  airport_latlng: [number, number];
  airport_main_gateway: boolean;
  airport_country: string;
};

export type TSAccount = {
  id: string;
  account_id: string;
  name: string;
  highlight_until: number;
};

export type TSAirline = {
  id: string;
  airline_name: string;
  airline_id: string;
};

export type TSAircraft = {
  id: string;
  aircraft_name: string;
  aircraft_type: string;
};

export type TSContact = {
  id: string;
  created_at: number;
  updated_at?: number;
  preferred_name?: string;
  full_name?: string;
  email: string;
  phone?: string;
  associations: {
    tags?: string[];
    accounts?: RequireOnly<Account, 'id' | 'name'>[];
    airlines?: string[];
    airports?: string[];
    has_airports: boolean;
    has_accounts: boolean;
    has_tags: boolean;
  }[];
};

export type TSRequest = {
  id: string;
  formatted_id: string;
  account_id: string;
  account_type: string;
  request_status: string;
  origins: string[];
  destinations: string[];
  author_email: string;
  created_at: number;
  dropoff_by: number;
  is_known_shipper: boolean;
  is_freighter_required: boolean;
  cargo_type: string;
  quote_status_counts: Record<QuoteStatus, number>;
  color?: string;
  classifications?: { name: string; color: string }[];
  main_request_id: string;
  linked_request_ids: string[];
  awb_numbers: string[];
  weight_break: number;
  payment_status: string;
  shipment_status: string;
};

// Manually Created Types

export enum UnNumber {
  UN3480 = '3480',
  UN3481 = '3481',
  UN3090 = '3090',
  UN3091 = '3091',
  UN3171 = '3171',
}

export enum BatteryTypeVariant {
  LiIon_UN3481_PI967 = 'LiIon_UN3481_PI967',
  LiIon_UN3481_PI966 = 'LiIon_UN3481_PI966',
  LiMetal_UN3091_PI969 = 'LiMetal_UN3091_PI969',
  LiMetal_UN3091_PI970 = 'LiMetal_UN3091_PI970',
}

export type Address = {
  // Business or recipient name
  recipient?: string;
  // Main address
  line_one?: string;
  // Apt/building number
  line_two?: string;
  // Country name (i.e. Canada, Germany, etc.)
  country?: string;
  // Province or State name (i.e. BC, Washington, etc.)
  state?: string;
  // City name (i.e. Victoria, Kelowna, etc.)
  city?: string;
  // Zip or postal code (i.e. V8X0B7, 10295, etc.)
  zip_code?: string;
};

export enum EstimatedTransitTime {
  OneToTwoDays = '1-2 days',
  TwoToThreeDays = '2-3 days',
  ThreeToFourDays = '3-4 days',
  FourToFiveDays = '4-5 days',
  FiveToSixDays = '5-6 days',
  SixToSevenDays = '6-7 days',
  SevenDaysPlus = '7+ days',
}

export enum ClarificationReason {
  IncorrectDetails = 'IncorrectDetails',
  Oversize = 'Oversize',
  DangerousGoods = 'DangerousGoods',
  TemperatureControl = 'TemperatureControl',
  Other = 'Other',
}

export enum CancelReason {
  CargoDelay = 'CargoDelay',
  TransitTime = 'TransitTime',
  Rate = 'Rate',
  Unknown = 'Unknown',
}

export type SelectOption = {
  label: string;
  value: string;
};

export type MaxAircraftParams = {
  length: number;
  width: number;
  height: number;
  weight_per_piece: number;
};

export type Hint = {
  name: string;
  displayed_at: Date | null;
  content: {
    title: string;
    description: string;
  };
};
export enum QuoteStage {
  ZERO = 'ZERO',
  ONE = 'ONE',
  TWO = 'TWO',
  THREE = 'THREE',
  FOUR = 'FOUR',
}

export enum DrawerType {
  ProviderQuoting = 'ProviderQuoting',
  QuoteUpdate = 'QuoteUpdate',
  AirlineBooking = 'AirlineBooking',
  BookingConfirmation = 'BookingConfirmation',
  BookingChange = 'BookingChange',
  BookingCancel = 'BookingCancel',
  NewRate = 'NewRate',
  Contact = 'Contact',
  RequestClarifications = 'RequestClarifications',
  RequestSchedule = 'RequestSchedule',
}

export enum WeightType {
  PerPiece = 'Weight Per Piece',
  Gross = 'Gross Weight',
}

export type FormType = 'Create' | 'Update';

export type CargoItem = {
  quantity: number;
  length: number;
  width: number;
  height: number;
  weight: number;
  handling: HandlingOptions;
  packing: HandlingOptions[];
};
